import * as math from "mathjs";
import React from "react";
import { price, round2 } from "../../../utils";
import { getPricesSteps } from "../../../utils_price";
import { useAuth } from "../../Auth/useAuth";
import useTranslations from "../../Translations/useTranslations";
import { useCart } from "../useCart";
import "./CurrentCartItems.css";
import ItemController from "./ItemController";

function ProductItems({
  isQuickDispatchItem = false,
  disableControls = false,
}) {
  const [{ user }] = useAuth();
  const [{ current_cart }, cartSDK] = useCart(user.client_profile?.id);
  const trl = useTranslations();
  const CART_KEY_PLV = "PLV";
  if (!current_cart) return null;
  const packsNames = current_cart.packs.map((pk) => pk.name) || [];
  const filteredProductNames = Object.keys(current_cart.products)?.filter(
    (key) =>
      !packsNames.includes(key) &&
      key !== CART_KEY_PLV &&
      current_cart.products[key].length > 0,
    // Filter quick dispatch items todo
  );

  if (filteredProductNames.length === 0) return null;

  const currentCartProductItems =
    filteredProductNames
      .map((key) => {
        return { [key]: current_cart.products[key] };
      })
      .flat()
      .reduce((a, b) => ({ ...a, ...b }), {}) || {};

  function _addToLocalCart(codgen, article, qte_commande) {
    let _cart = currentCartProductItems[codgen] || [];
    let index = _cart.findIndex((art) => art.article.CODART === article.CODART);

    let remise =
      article.REMISE_FIXE > 0
        ? article.REMISE_FIXE
        : article.groupe_tarifaire.REMISE_FIXE > 0
          ? article.groupe_tarifaire.REMISE_FIXE
          : null;

    if (!remise || (!!remise && remise <= 0)) {
      if (
        article.groupe_tarifaire.TRANCHE5 > 0 &&
        qte_commande >= article.groupe_tarifaire.TRANCHE5
      ) {
        remise = article.groupe_tarifaire.REMISE5;
      } else if (
        article.groupe_tarifaire.TRANCHE4 > 0 &&
        qte_commande >= article.groupe_tarifaire.TRANCHE4
      ) {
        remise = article.groupe_tarifaire.REMISE4;
      } else if (
        article.groupe_tarifaire.TRANCHE3 > 0 &&
        qte_commande >= article.groupe_tarifaire.TRANCHE3
      ) {
        remise = article.groupe_tarifaire.REMISE3;
      } else if (
        article.groupe_tarifaire.TRANCHE2 > 0 &&
        qte_commande >= article.groupe_tarifaire.TRANCHE2
      ) {
        remise = article.groupe_tarifaire.REMISE2;
      } else if (
        article.groupe_tarifaire.TRANCHE1 > 0 &&
        qte_commande >= article.groupe_tarifaire.TRANCHE1
      ) {
        remise = article.groupe_tarifaire.REMISE1;
      } else {
        remise = 0;
      }
    }

    const { pricesSteps, nbUnit, minIncrement, priceStep, priceStepRef } =
      getPricesSteps(article, qte_commande);

    const qte_unite_de_vente = qte_commande / (article.up_vte || 1);
    const price_applied = priceStep ? priceStep.price * qte_unite_de_vente : 0;

    if (index >= 0) {
      // It already there
      if (qte_commande > 0) {
        _cart[index] = {
          article,
          qte_commande,
          custom_remise: remise,
          qte_unite_de_vente,
          price_applied,
          pub: priceStep?.price || 0,
        };
      } else {
        _cart.splice(index, 1);
      }
    } else {
      _cart.push({
        article,
        qte_commande,
        custom_remise: remise,
        qte_unite_de_vente,
        price_applied,
        pub: priceStep?.price || 0,
      });
    }

    cartSDK.addInCart({
      creator: user.client_profile?.id,
      product: {
        [codgen]: _cart.sort((item1, item2) => {
          const item1Order = item1.article.ORDRE;
          const item2Order = item2.article.ORDRE;

          if (item1Order < item2Order) return -1;
          if (item1Order > item2Order) return 1;

          return 0;
        }),
      },
    });
  }

  function _removeProduct(codgen, codart) {
    let products = currentCartProductItems[codgen] || [];
    cartSDK.addInCart({
      creator: user.client_profile?.id,
      product: {
        [codgen]: products.filter((prod) => prod.article.CODART !== codart),
      },
    });
  }

  function renderItem(codgen, product) {
    const article = product.article;
    let qte_commande = 0;
    if (product.qte_commande) {
      qte_commande = product.qte_commande;
    }

    const remise =
      product.custom_remise >= 0
        ? product.custom_remise
        : product.article.REMISE_FIXE > 0
          ? product.article.REMISE_FIXE
          : product.article.groupe_tarifaire.REMISE_FIXE > 0
            ? product.article.groupe_tarifaire.REMISE_FIXE
            : null;

    let realPUB = product.article.PUB - (article.PUB * remise) / 100;

    const { pricesSteps, nbUnit, minIncrement, priceStep, priceStepRef } =
      getPricesSteps(product.article, product.qte_commande);
    let totalPrice = (
      priceStep
        ? round2((priceStep.price / nbUnit) * (1 - remise / 100)) *
          product.qte_commande
        : 0
    ).toFixed(2);

    if (product.article.prices && priceStep) {
      realPUB = priceStep.price / nbUnit;
      totalPrice = round2(realPUB * (1 - remise / 100)) * product.qte_commande;
    }

    let remiseCalculated = remise;

    if (article.prices?.length > 0 && priceStep && priceStepRef) {
      const tieredRemise = Math.max(
        math.round((1 - priceStep.price / priceStepRef.price) * 100, 2),
        0,
      );
      remiseCalculated = Math.max(remiseCalculated, tieredRemise);
    }
    if (article.prices?.length > 0 && !article.pricesref) {
      // if article and no prices ref => use is in net price => no remise displayed
      remiseCalculated = 0;
    }

    const productLabel = trl.data.message(
      "ART",
      "CODART",
      article.CODART,
      "LIBART",
    );

    const minOrderCount = minIncrement || 1;

    function handleAdd() {
      _addToLocalCart(codgen, article, qte_commande + minOrderCount);
    }

    function handleEmty() {
      if (!window.confirm(trl.message("Cart.removeProductOnAction"))) return;
      if (qte_commande === 0) return;
      _removeProduct(codgen, article.CODART);
    }

    function handleRemove() {
      if (qte_commande - 1 < 0) return;
      _addToLocalCart(codgen, article, qte_commande - minOrderCount);
    }
    return (
      <React.Fragment>
        <ItemController
          addAction={handleAdd}
          removeAction={handleRemove}
          emptyAction={handleEmty}
          count={qte_commande}
          minOrderCount={minOrderCount}
          itemName={productLabel}
          discountLabel={remiseCalculated > 0 ? remiseCalculated : null}
          currentPrice={price(round2(totalPrice))}
          disableControls={disableControls}
        />
      </React.Fragment>
    );
  }

  const headerStyle = {
    fontFamily: "Quicksand, sans-serif",
    fontSize: "15px",
    fontWeight: "700",
    color: "#000",
    margin: "0.5rem 1rem 0.5rem 1.2rem",
  };
  const productItemsContainerStyle = {
    display: "block",
  };
  return (
    <React.Fragment>
      {filteredProductNames.map((codgen, i) => {
        return (
          <React.Fragment key={i}>
            <div style={headerStyle}>{codgen}</div>
            <div style={productItemsContainerStyle}>
              {currentCartProductItems[codgen].map((product, idx) => (
                <React.Fragment key={product.article.CODART}>
                  {renderItem(codgen, product)}
                </React.Fragment>
              ))}
            </div>
          </React.Fragment>
        );
      })}
    </React.Fragment>
  );
}

export default ProductItems;
